import { Box, Typography, FormControlLabel, Checkbox } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import { getPartners } from '../../../containers/PartnerAndMembers/api';
import { FEATURE_LEVEL, hasFeatureEnabled } from '../../../utils/featureConstants';
import AsyncAutoComplete from '../../common/AsyncAutoSuggest';
import FormsPopup from '../../common/FormsPopup/FormsPopup';
import InputField from '../../common/InputField/InputField';
import styles from './styles';
import { CreateMemberProps } from './types';
import USState from '../../../utils/USState';
import CustomSelect from '../../common/Select';
import FormButton from '../../common/FormButton';

const CreateMember = ({
    open,
    handleInput,
    handleSubmit,
    handleCloseCreatePop,
    fromSelectedPartner,
    formValues,
    formErrors,
    defaultSelectedPartner,
    setFormValues,
    onKitNeedChange,
    submitting,
}: CreateMemberProps) => {
    return (
        <FormsPopup
            id="create_member"
            open={open}
            title="Create New Member"
            textAlign="left"
            handleClose={handleCloseCreatePop}
            description={<></>}
            width={474}
        >
            <Box component="form" noValidate onSubmit={handleSubmit}>
                <Typography component="div">
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={formValues?.needs_welcome_kit === '1'}
                                onChange={(event) => {
                                    if (event.target.checked) onKitNeedChange('1');
                                }}
                                icon={<img alt="uncheck circle" src="/images/uncheck_cirlce.svg" />}
                                checkedIcon={<img alt="check circle" src="/images/check_cirlce.svg" />}
                            />
                        }
                        label={<Typography style={styles.plain_text}>Send Welcome Kit</Typography>}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={formValues?.needs_welcome_kit === '0'}
                                onChange={(event) => {
                                    if (event.target.checked) onKitNeedChange('0');
                                }}
                                icon={<img alt="uncheck circle" src="/images/uncheck_cirlce.svg" />}
                                checkedIcon={<img alt="check circle" src="/images/check_cirlce.svg" />}
                            />
                        }
                        label={<Typography style={styles.plain_text}>Do not send Welcome Kit</Typography>}
                    />
                </Typography>
                <Typography component="div" sx={styles.mt_24}>
                    <AsyncAutoComplete
                        id="selectedPartner"
                        async={async (value) => {
                            const res = await getPartners({
                                currentPage: 1,
                                partnerSearchText: value,
                                partnerSize: 10000,
                                selectedPartnerPeriod: '',
                                selectedPartnerStatus: 'active',
                                sort: 'name',
                                order: 'asc',
                            });
                            return res.data.data.rows;
                        }}
                        label="Selected Partner"
                        optionLabel="name"
                        defaultValue={{
                            name: defaultSelectedPartner?.name || '',
                            id: defaultSelectedPartner?.id || '',
                            uniqueId: defaultSelectedPartner?.uniqueId || '',
                        }}
                        handleSelectedoption={(selectedOption) => {
                            if (formValues && setFormValues) {
                                setFormValues({
                                    ...formValues,
                                    selectedPartnerId: selectedOption?.id ? selectedOption?.id : '',
                                    employerId: selectedOption?.uniqueId ? selectedOption?.uniqueId : '',
                                });
                            }
                        }}
                        error={formErrors && formErrors.selectedPartnerId && formErrors.selectedPartnerId[0] !== ''}
                        errorMessage={formErrors && formErrors.selectedPartnerId && formErrors.selectedPartnerId[0]}
                        disabled={fromSelectedPartner}
                    />
                </Typography>
                <Typography component="div" sx={styles.mt_24}>
                    <InputField
                        required
                        id="firstname"
                        label="First Name*"
                        type="text"
                        name="firstname"
                        placeholder="E.g. Marcos"
                        value={formValues?.firstname}
                        onChange={handleInput}
                        error={formErrors && formErrors.firstname && formErrors.firstname[0] !== ''}
                        errorMessage={formErrors && formErrors.firstname && formErrors.firstname[0]}
                    />
                </Typography>
                <Typography component="div" sx={styles.mt_24}>
                    <InputField
                        required
                        id="lastname"
                        label="Last Name*"
                        type="text"
                        name="lastname"
                        placeholder="E.g. Castro"
                        value={formValues?.lastname}
                        onChange={handleInput}
                        error={formErrors && formErrors.lastname && formErrors.lastname[0] !== ''}
                        errorMessage={formErrors && formErrors.lastname && formErrors.lastname[0]}
                    />
                </Typography>
                <Typography component="div" sx={styles.mt_24}>
                    <InputField
                        required
                        name="email"
                        label="Email*"
                        type="text"
                        id="email"
                        placeholder="@email.com"
                        autoComplete="Email"
                        value={formValues?.email}
                        onChange={handleInput}
                        error={formErrors && formErrors.email && formErrors.email[0] !== ''}
                        errorMessage={formErrors && formErrors.email && formErrors.email[0]}
                    />
                </Typography>
                <Typography sx={styles.mt_24} component="div">
                    <Typography component="div">
                        <InputField
                            required
                            id="phone"
                            label="Phone Number*"
                            type="text"
                            name="phone"
                            placeholder="Type here..."
                            value={formValues?.phone}
                            onChange={handleInput}
                            error={formErrors && formErrors.phone && formErrors.phone[0] !== ''}
                            errorMessage={formErrors && formErrors.phone && formErrors.phone[0]}
                        />
                    </Typography>
                    <Typography sx={styles.mt_24} component="div">
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                            <DatePicker
                                inputFormat="MM/DD/YYYY"
                                value={
                                    formValues?.dateOfBirth ? moment(formValues.dateOfBirth).format('MM/DD/YYYY') : ''
                                }
                                onChange={(newValue) => {
                                    if (formValues && setFormValues) {
                                        setFormValues({
                                            ...formValues,
                                            dateOfBirth: moment(newValue).format('MM/DD/YYYY'),
                                        });
                                    }
                                }}
                                label="Date of Birth*"
                                maxDate={new Date()}
                                renderInput={(params) => (
                                    <InputField
                                        {...params}
                                        required={false}
                                        id="dateOfBirth"
                                        name="dateOfBirth"
                                        value={
                                            formValues?.dateOfBirth
                                                ? moment(formValues.dateOfBirth).format('MM/DD/YYYY')
                                                : ''
                                        }
                                        error={formErrors && formErrors.dateOfBirth && formErrors.dateOfBirth[0] !== ''}
                                        errorMessage={formErrors && formErrors.dateOfBirth && formErrors.dateOfBirth[0]}
                                        placeholder="MM/DD/YYYY"
                                    />
                                )}
                            />
                        </LocalizationProvider>
                    </Typography>
                    <Typography sx={styles.mt_24} component="div">
                        <InputField
                            required
                            id="address1"
                            label="Address*"
                            type="text"
                            name="address1"
                            placeholder="Type here..."
                            value={formValues?.address1}
                            onChange={handleInput}
                            error={formErrors && formErrors.address1 && formErrors.address1[0] !== ''}
                            errorMessage={formErrors && formErrors.address1 && formErrors.address1[0]}
                        />
                    </Typography>
                    <Typography sx={styles.mt_24} component="div">
                        <InputField
                            required
                            id="address2"
                            label="Apt Number"
                            type="text"
                            name="address2"
                            placeholder="Type here..."
                            value={formValues?.address2}
                            onChange={handleInput}
                            error={formErrors && formErrors.address2 && formErrors.address2[0] !== ''}
                            errorMessage={formErrors && formErrors.address2 && formErrors.address2[0]}
                        />
                    </Typography>
                    <Typography sx={styles.mt_24} component="div">
                        <InputField
                            required
                            id="city"
                            label="City*"
                            type="text"
                            name="city"
                            placeholder="Type here..."
                            onChange={handleInput}
                            error={formErrors && formErrors.city && formErrors.city[0] !== ''}
                            errorMessage={formErrors && formErrors.city && formErrors.city[0]}
                        />
                    </Typography>
                    <Typography sx={styles.mt_24} component="div">
                        <CustomSelect
                            name="state"
                            label="State*"
                            id="state"
                            handleOnchange={handleInput}
                            items={USState}
                            value={formValues?.state}
                            error={formErrors && formErrors.state && formErrors.state[0] !== ''}
                            errorMessage={formErrors && formErrors.state && formErrors.state[0]}
                            customStyles={{ height: 44 }}
                        />
                    </Typography>
                    <Typography sx={styles.mt_24} component="div">
                        <InputField
                            required
                            id="zip"
                            label="Zip Code*"
                            type="text"
                            name="zip"
                            placeholder="Type here..."
                            value={formValues?.zip}
                            onChange={handleInput}
                            error={formErrors && formErrors.zip && formErrors.zip[0] !== ''}
                            errorMessage={formErrors && formErrors.zip && formErrors.zip[0]}
                        />
                    </Typography>
                </Typography>
                {hasFeatureEnabled(FEATURE_LEVEL.viewPartnerEmployer) && (
                    <FormButton
                        id="send_invitation_email"
                        type="submit"
                        fullWidth
                        variant="contained"
                        sx={styles.button}
                        submitting={submitting}
                    >
                        Save Member
                    </FormButton>
                )}
            </Box>
        </FormsPopup>
    );
};

export default CreateMember;
